/**
 * SiteMapPicker
 * ─────────────────────────────────────────────────────────────────────────────
 * Flow:
 *   1. User types address in "Searched Location" box → Enter / Search button
 *   2. Geocoder finds lat, lng, formatted address
 *   3. Marker moves on map, circle updates, parent form fields auto-fill
 *   4. User can also drag the marker / click map → reverse geocode updates fields
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    GoogleMap,
    useLoadScript,
    Marker,
    Circle,
} from '@react-google-maps/api';
import { Search } from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const LIBRARIES: ('places')[] = ['places'];

const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 22.7196, lng: 75.8577 };

const MAP_CONTAINER_STYLE: React.CSSProperties = {
    width: '100%',
    height: '400px',
    borderRadius: '0.75rem',
    overflow: 'hidden',
};

const CIRCLE_OPTIONS = {
    strokeColor: '#4F46E5',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#4F46E5',
    fillOpacity: 0.15,
};

// ── Types ─────────────────────────────────────────────────────────────────────
export interface MapPickerResult {
    lat: number;
    lng: number;
    searchedLocation: string;
    address: string;
}

interface SiteMapPickerProps {
    lat?: number;
    lng?: number;
    range?: number | string;
    searchedLocation?: string;
    /** External validation error (e.g. "Location is required") from parent form */
    searchedLocationError?: string;
    onChange: (result: MapPickerResult) => void;
}

// ── Helper: Reverse geocode (lat/lng → address) ───────────────────────────────
function reverseGeocode(lat: number, lng: number): Promise<{ address: string; searchedLocation: string }> {
    return new Promise((resolve) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                resolve({
                    address: results[0].formatted_address,
                    searchedLocation: results[0].formatted_address,
                });
            } else {
                resolve({ address: '', searchedLocation: '' });
            }
        });
    });
}

// ── Helper: Forward geocode (address → lat/lng) ───────────────────────────────
function forwardGeocode(address: string): Promise<{ lat: number; lng: number; formattedAddress: string } | null> {
    return new Promise((resolve) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
                resolve({
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng(),
                    formattedAddress: results[0].formatted_address,
                });
            } else {
                resolve(null);
            }
        });
    });
}

// ── Inner map component (rendered only after Google Maps script is loaded) ─────
interface MapInnerProps {
    initialLat?: number;
    initialLng?: number;
    range?: number | string;
    searchedLocation?: string;
    searchedLocationError?: string;
    onChange: (result: MapPickerResult) => void;
}

const MapInner: React.FC<MapInnerProps> = ({ initialLat, initialLng, range, searchedLocation, searchedLocationError, onChange }) => {
    // ── State ─────────────────────────────────────────────────────────────────
    const [position, setPosition] = useState<google.maps.LatLngLiteral>(
        initialLat && initialLng ? { lat: initialLat, lng: initialLng } : DEFAULT_CENTER
    );
    const [searchText, setSearchText] = useState(searchedLocation || '');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState('');

    // ── Refs ──────────────────────────────────────────────────────────────────
    const mapRef = useRef<google.maps.Map | null>(null);
    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    // Sync search text when external searchedLocation arrives
    useEffect(() => {
        if (searchedLocation !== undefined) {
            setSearchText(searchedLocation);
        }
    }, [searchedLocation]);

    // Sync marker when edit-mode lat/lng arrive after API fetch
    useEffect(() => {
        if (initialLat && initialLng) {
            const pos = { lat: initialLat, lng: initialLng };
            setPosition(pos);
            mapRef.current?.panTo(pos);
        }
    }, [initialLat, initialLng]);

    // ── Core: update position + notify parent ─────────────────────────────────
    const moveTo = async (lat: number, lng: number, searchedLoc = '', addr = '') => {
        const pos = { lat, lng };
        setPosition(pos);
        mapRef.current?.panTo(pos);
        mapRef.current?.setZoom(15);

        // If address not provided (drag / click), reverse-geocode
        if (!addr) {
            const geo = await reverseGeocode(lat, lng);
            onChangeRef.current({ lat, lng, searchedLocation: geo.searchedLocation, address: geo.address });
            setSearchText(geo.searchedLocation);
        } else {
            onChangeRef.current({ lat, lng, searchedLocation: searchedLoc, address: addr });
        }
    };

    // ── Search: forward geocode the typed address ─────────────────────────────
    const handleSearch = async () => {
        const query = searchText.trim();
        if (!query) {
            setSearchError("Location search is required. Please enter an address to search.");
            return;
        }

        // Disallow special characters that are not standard for addresses
        if (/[^a-zA-Z0-9\s,./#()'-]/.test(query)) {
            setSearchError("Special characters (e.g. @, $, %, *, !) are not allowed in location search.");
            return;
        }

        setIsSearching(true);
        setSearchError('');

        const result = await forwardGeocode(query);

        if (result) {
            await moveTo(result.lat, result.lng, query, result.formattedAddress);
            setSearchText(result.formattedAddress); // update search box with exact address
        } else {
            setSearchError('Location not found. Try a more specific address.');
        }

        setIsSearching(false);
    };

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        if (/[^a-zA-Z0-9\s,./#()'-]/.test(raw)) {
            setSearchError("Special characters (e.g. @, $, %, *, !) are not allowed");
        } else if (!raw.trim()) {
            setSearchError("Location search is required. Please enter an address to search.");
        } else {
            setSearchError('');
        }
        const clean = raw.replace(/[^a-zA-Z0-9\s,./#()'-]/g, "");
        setSearchText(clean);

        // If user clears the search input, immediately clear the parent mapResult so form cannot be submitted
        if (!clean.trim()) {
            onChangeRef.current({
                lat: 0,
                lng: 0,
                searchedLocation: '',
                address: '',
            });
        }
    };

    // Enter key in search box → trigger search
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    // ── Marker drag end ───────────────────────────────────────────────────────
    const handleMarkerDragEnd = async (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        await moveTo(e.latLng.lat(), e.latLng.lng());
    };

    // ── Map click → move marker ───────────────────────────────────────────────
    const handleMapClick = async (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;
        await moveTo(e.latLng.lat(), e.latLng.lng());
    };

    const circleRadius = Number(range) || 0;

    // The combined display error: internal geocode error OR external validation error
    const displayError = searchError || searchedLocationError;

    return (
        <div className="space-y-3">
            {/* ── Search Box ── */}
            <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                    Search Location on Map <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            value={searchText}
                            onChange={handleSearchInputChange}
                            onKeyDown={handleKeyDown}
                            maxLength={150}
                            placeholder="Type address and press Enter or click Search (e.g. Indore, MP)"
                            className={`w-full h-10 pl-3 pr-10 rounded-lg border bg-white text-sm shadow-sm focus:outline-none focus:ring-1 transition-colors ${
                                displayError
                                    ? 'border-red-400 focus:ring-red-400'
                                    : 'border-input focus:ring-primary'
                            }`}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleSearch}
                        disabled={isSearching}
                        className="h-10 px-4 flex items-center gap-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                        {isSearching
                            ? <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <Search size={16} />
                        }
                        {isSearching ? 'Searching...' : 'Search'}
                    </button>
                </div>
            </div>

            {/* Error (geocode fail OR form validation) */}
            {displayError && (
                <p className="text-xs text-red-500 -mt-1">{displayError}</p>
            )}

            {/* ── Google Map ── */}
            <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                zoom={13}
                center={position}
                onLoad={(map) => { mapRef.current = map; }}
                onClick={handleMapClick}
                options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                }}
            >
                {/* Draggable Marker */}
                <Marker
                    position={position}
                    draggable
                    onDragEnd={handleMarkerDragEnd}
                    title="Drag to adjust location"
                />

                {/* Range Circle */}
                {circleRadius > 0 && (
                    <Circle
                        center={position}
                        radius={circleRadius}
                        options={CIRCLE_OPTIONS}
                    />
                )}
            </GoogleMap>

            <p className="text-xs text-gray-400">
                📍 Type address → press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono text-[10px]">Enter</kbd> or click Search. You can also click on the map or drag the marker. The blue circle shows the attendance range.
            </p>
        </div>
    );
};

// ── Main export ───────────────────────────────────────────────────────────────
const SiteMapPicker: React.FC<SiteMapPickerProps> = ({ lat, lng, range, searchedLocation, searchedLocationError, onChange }) => {
    const apiKey = import.meta.env.VITE_APP_GOOGLE_API_KEY as string;

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries: LIBRARIES,
    });

    if (loadError) {
        return (
            <div className="flex items-center justify-center h-40 rounded-xl bg-red-50 text-red-500 text-sm">
                Failed to load Google Maps. Check your API key.
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="flex items-center justify-center h-40 rounded-xl bg-gray-50 text-gray-400 text-sm">
                <span className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                Loading map...
            </div>
        );
    }

    return (
        <MapInner
            initialLat={lat}
            initialLng={lng}
            range={range}
            searchedLocation={searchedLocation}
            searchedLocationError={searchedLocationError}
            onChange={onChange}
        />
    );
};

export default SiteMapPicker;
